<script setup>
import { ref, watch, computed, onMounted } from 'vue';
import { useAlert } from 'dashboard/composables';
import Input from '../../../../../components-next/input/Input.vue';
import Textarea from '../../../../../components-next/textarea/TextArea.vue';
import Button from '../../../../../components-next/button/Button.vue';
import ValidationFeedback from '../shared/ValidationFeedback.vue';

const props = defineProps({
  items: {
    type: Array,
    default: () => [],
  },
  nameLabel: {
    type: String,
    default: 'Nome',
  },
  nameRequired: {
    type: Boolean,
    default: true, // Campos são obrigatórios por padrão quando adicionados
  },
  namePlaceholder: {
    type: String,
    default: 'Digite o nome',
  },
  valueLabel: {
    type: String,
    default: 'Valor',
  },
  valueRequired: {
    type: Boolean,
    default: true, // Campos são obrigatórios por padrão quando adicionados
  },
  valuePlaceholder: {
    type: String,
    default: 'Digite o valor',
  },
  addButtonLabel: {
    type: String,
    default: 'Adicionar Item',
  },
  isMultiline: {
    type: Boolean,
    default: false,
  },
  // Prop para determinar se é uma política ou campo adicional
  itemType: {
    type: String,
    default: 'item' // Pode ser 'policy' ou 'field'
  },
  // Nova prop para detectar modo de edição
  isEditMode: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['update', 'validation']);

const localItems = ref([...props.items]);
// Array para armazenar quais campos foram tocados
const touchedItems = ref([]);
// Flag para controlar se o alerta já foi mostrado
const hasShownFieldAlert = ref(false);

// Computed para verificar se todos os itens são válidos
const areAllItemsValid = computed(() => {
  // Se não houver itens, é considerado válido
  if (localItems.value.length === 0) return true;
  
  // Verifica se todos os itens estão completamente preenchidos
  const allItemsComplete = localItems.value.every(item => {
    const hasName = item.name && item.name.trim().length > 0;
    const hasValue = item.value && item.value.trim().length > 0;
    
    // Ambos os campos devem estar preenchidos
    return hasName && hasValue;
  });
  
  // Se nem todos os itens estão completos, é inválido
  if (!allItemsComplete) return false;
  
  // Verifica se algum item tem erro de validação
  const hasErrors = localItems.value.some(item => 
    item.nameError || item.valueError
  );
  
  // Se não houver erros, é válido
  return !hasErrors;
});

// Emite o estado de validação sempre que muda
watch(areAllItemsValid, (newValue) => {
  emit('validation', newValue);
});

// Emite o estado inicial na montagem
onMounted(() => {
  // Inicializa localItems com nameError e valueError
  localItems.value.forEach(item => {
    if (!item.hasOwnProperty('nameError')) item.nameError = '';
    if (!item.hasOwnProperty('valueError')) item.valueError = '';
  });
  
  // Inicializa touchedItems como false para todos os campos existentes
  touchedItems.value = new Array(localItems.value.length).fill().map(() => ({ 
    name: false, 
    value: false 
  }));
  
  // Atualiza o estado de validação sem mostrar erros
  emit('validation', areAllItemsValid.value);
});

// Mostra um alerta de ajuda quando o usuário interage com um campo
const showFieldHelpAlert = () => {
  if (!hasShownFieldAlert.value) {
    const message = props.itemType === 'policy' 
      ? 'Descreva detalhadamente as políticas da empresa, pois elas serão usadas pelo agente para responder dúvidas sobre termos de uso, garantias, devoluções e outras informações importantes.'
      : 'Preencha os campos adicionais com atenção. Estas informações serão usadas pelo agente para fornecer respostas mais precisas e personalizadas aos seus clientes.';
    
    useAlert(message, { type: 'info', duration: 6000 });
    hasShownFieldAlert.value = true;
  }
};

// Verifica e valida todos os itens, mas só mostra erros nos tocados
const checkAndValidateAllItems = () => {
  localItems.value.forEach((item, index) => {
    const hasName = item.name && item.name.trim().length > 0;
    const hasValue = item.value && item.value.trim().length > 0;
    
    // Só define erros se o campo foi tocado
    if (touchedItems.value[index]?.name) {
      if (!hasName) {
        item.nameError = props.itemType === 'policy' ? 
          'Nome da política é obrigatório' : 
          'Nome do campo é obrigatório';
      } else {
        item.nameError = '';
      }
    }
    
    if (touchedItems.value[index]?.value) {
      if (!hasValue) {
        item.valueError = props.itemType === 'policy' ? 
          'Conteúdo da política é obrigatório' : 
          'Valor do campo é obrigatório';
      } else {
        item.valueError = '';
      }
    }
  });
  
  // Emite update após validação
  updateItems();
};

const removeItem = (index) => {
  localItems.value.splice(index, 1);
  touchedItems.value.splice(index, 1);
  updateItems();
};

const handleInput = (index, field) => {
  // Se o campo foi tocado, valida em tempo real
  if (touchedItems.value[index]?.[field]) {
    validateField(index, field);
  }
  
  // Verifica o outro campo também, para validação cruzada
  const otherField = field === 'name' ? 'value' : 'name';
  if (touchedItems.value[index]?.[otherField]) {
    validateField(index, otherField);
  }
};

const handleBlur = (index, field) => {
  // Marca o campo como tocado
  if (!touchedItems.value[index]) {
    touchedItems.value[index] = { name: false, value: false };
  }
  
  touchedItems.value[index][field] = true;
  
  // Valida o campo
  validateField(index, field);
};

const handleFocus = () => {
  // Mostra o alerta de ajuda quando o usuário foca em qualquer campo
  showFieldHelpAlert();
};

const validateField = (index, field) => {
  const item = localItems.value[index];
  
  // Só valida se o campo já foi tocado
  if (!touchedItems.value[index] || !touchedItems.value[index][field]) {
    return;
  }
  
  // Verifica se há nome
  const hasName = item.name && item.name.trim().length > 0;
  // Verifica se há valor
  const hasValue = item.value && item.value.trim().length > 0;
  
  if (field === 'name') {
    // Nome é sempre obrigatório quando um item é adicionado
    if (!hasName) {
      item.nameError = props.itemType === 'policy' ? 
        'Nome da política é obrigatório' : 
        'Nome do campo é obrigatório';
    } else {
      item.nameError = '';
    }
  }
  
  if (field === 'value') {
    // Valor é sempre obrigatório quando um item é adicionado
    if (!hasValue) {
      item.valueError = props.itemType === 'policy' ? 
        'Conteúdo da política é obrigatório' : 
        'Valor do campo é obrigatório';
    } else {
      item.valueError = '';
    }
  }
  
  // Emite atualização após validação
  updateItems();
};

const updateItems = () => {
  emit('update', localItems.value);
};

// Observe os itens para sincronizar com as props
watch(() => props.items, (newItems) => {
  if (JSON.stringify(newItems) !== JSON.stringify(localItems.value)) {
    localItems.value = [...newItems];
    
    // Inicializa as flags de erro para cada item
    localItems.value.forEach(item => {
      if (!item.hasOwnProperty('nameError')) item.nameError = '';
      if (!item.hasOwnProperty('valueError')) item.valueError = '';
    });
    
    // Reset touched status for new items
    touchedItems.value = new Array(newItems.length).fill().map(() => ({ name: false, value: false }));
    
    // Emite o estado de validação ao receber novos itens
    emit('validation', areAllItemsValid.value);
  }
}, { deep: true });

// Watch local items for changes to emit updates
watch(localItems, () => {
  updateItems();
}, { deep: true });
</script>

<template>
  <div class="space-y-4">
    <TransitionGroup name="list-fade" tag="div">
      <div 
        v-for="(item, index) in localItems" 
        :key="`dynamic-field-${index}`" 
        class="relative bg-n-slate-2 dark:bg-n-slate-12/5 p-4 rounded-lg border border-n-slate-5 dark:border-n-slate-10/20 mt-7 dynamic-field-item dynamic-field-container"
      >
        <!-- Cabeçalho -->
        <div class="flex items-center justify-between mb-3">
          <h4 class="text-sm font-medium text-n-slate-12">
            {{ localItems.length > 1 ? `${props.itemType === 'policy' ? 'Política' : 'Campo'} ${localItems.length - index}` : props.itemType === 'policy' ? 'Política' : 'Campo' }}
          </h4>
          <Button
            icon="i-lucide-trash-2"
            variant="faded"
            color="ruby"
            size="xs"
            class="remove-button-rotate"
            @click="removeItem(index)"
            v-tooltip.top="`Remover ${props.itemType === 'policy' ? 'política' : 'campo'}`"
          />
        </div>
        
        <!-- Grid de campos -->
        <div class="space-y-3">
          <!-- Nome do campo -->
          <div>
            <label class="text-xs text-n-slate-11 mb-1 block">{{ nameLabel }} *</label>
            <Input
              v-model="item.name"
              :placeholder="namePlaceholder"
              :error="item.nameError"
              @focus="handleFocus"
              @blur="handleBlur(index, 'name')"
              @input="() => handleInput(index, 'name')"
            />
          </div>
          
          <!-- Valor/Conteúdo do campo - Sempre usando Textarea -->
          <div>
            <div class="flex items-center justify-between mb-1">
              <label class="text-xs text-n-slate-11">{{ valueLabel }} *</label>
              <span v-if="item.value" class="text-xs text-n-slate-10">{{ item.value.length }}</span>
            </div>
            <Textarea
              v-model="item.value"
              :placeholder="valuePlaceholder"
              :error="item.valueError"
              :rows="props.itemType === 'policy' ? 4 : 3"
              :autoHeight="true"
              :resize="true"
              :minHeight="'5rem'"
              :maxHeight="'15rem'"
              @focus="handleFocus"
              @blur="handleBlur(index, 'value')"
              @input="() => handleInput(index, 'value')"
            />
          </div>
        </div>
      </div>
    </TransitionGroup>
  </div>
</template>

<style lang="scss" scoped>
// Importar animações compartilhadas
@import '../shared/DynamicFieldAnimations.scss';

// Estilos específicos para este componente
.dynamic-field-item {
  // Adiciona transição suave ao card
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  }
}

// Usa a animação do arquivo compartilhado
// .remove-button-rotate está definido em DynamicFieldAnimations.scss
</style>